import axios from 'axios';
import userService from '@/services/user.service';

// 创建axios实例
const service = axios.create({
    baseURL: '/', // API的基础URL
    timeout: 15000, // 请求超时时间
});

// 标记是否正在刷新令牌
let isRefreshing = false;
// 等待令牌刷新的请求队列
let refreshSubscribers = [];

// 将请求加入等待队列
const subscribeTokenRefresh = (callback) => {
    refreshSubscribers.push(callback);
};

  // 获取当前登录用户
 const getCurrentUser = () => {
    const userJson = localStorage.getItem('user');
    if (!userJson) return null;
    
    try {
      return JSON.parse(userJson);
    } catch (e) {
      console.error('解析用户信息失败:', e);
      return null;
    }
  }
// 执行队列中的请求
const onRefreshed = (token) => {
    refreshSubscribers.forEach(callback => callback(token));
    refreshSubscribers = [];
};

// 显示登录提示
const showLoginModal = () => {
    window.dispatchEvent(new CustomEvent('auth-error', {
        detail: {
            loginRequired: true,
            message: '请先登录以继续操作'
        }
    }));
};

// 请求拦截器
service.interceptors.request.use(
    config => {
        // 记录请求信息
        const timestamp = new Date().toISOString();
        const requestId = Math.random().toString(36).substring(2, 10);
        
        // 开始详细日志（仅对关键请求）
        const isAuthRequest = config.url.includes('/auth/');
        const isCroomRequest = config.url.includes('/croom/');
        
        if (isAuthRequest || isCroomRequest) {
            console.group(`%c请求[${requestId}]: ${config.method.toUpperCase()} ${config.url}`, 
                        'background:#4caf50; color:#fff; padding:2px 4px;');
            console.log('请求时间:', timestamp);
            console.log('请求方法:', config.method.toUpperCase());
            console.log('请求URL:', config.url);
            console.log('请求参数:', config.params || '无');
            console.log('请求数据:', config.data || '无');
        }
        
        // 直接从localStorage获取最新的用户信息和令牌
        try {
            const userStr = localStorage.getItem('user');
            
            if (isAuthRequest || isCroomRequest) {
                console.log('localStorage中的user数据:', userStr ? '存在' : '不存在', '长度:', userStr?.length || 0);
            }
            
            if (userStr) {
                const userData = JSON.parse(userStr);
                if (userData && userData.access_token) {
                    // 使用最新的令牌
                    config.headers['Authorization'] = `Bearer ${userData.access_token}`;
                    
                    if (isAuthRequest || isCroomRequest) {
                        console.log('已添加Authorization头:', `Bearer ${userData.access_token.substring(0, 15)}...`);
                        console.log('令牌完整长度:', userData.access_token.length);
                    }
                } else {
                    if (isAuthRequest || isCroomRequest) {
                        console.warn('用户数据中无access_token');
                    }
                }
            } else {
                if (isAuthRequest || isCroomRequest) {
                    console.warn('localStorage中无user数据');
                }
            }
        } catch (e) {
            console.error('[请求] 获取令牌失败:', e);
        }
        
        if (isAuthRequest || isCroomRequest) {
            console.log('完整请求头:', config.headers);
            console.groupEnd();
        }
        
        return config;
    },
    error => {
        console.error('[Request Debug] 请求拦截器错误:', error);
        return Promise.reject(error);
    }
);

// 响应拦截器
service.interceptors.response.use(
    response => {
        const data = response.data;
        const config = response.config;
        
        // 记录响应信息（仅对关键请求）
        const isAuthRequest = config.url.includes('/auth/');
        const isCroomRequest = config.url.includes('/croom/');
        
        if (isAuthRequest || isCroomRequest) {
            console.group(`%c响应: ${config.method.toUpperCase()} ${config.url}`, 
                         'background:#2196f3; color:#fff; padding:2px 4px;');
            console.log('响应时间:', new Date().toISOString());
            console.log('响应状态码:', response.status);
            console.log('响应数据:', data);
            console.groupEnd();
        }
        
        // 适配后端统一Result响应格式
        // 如果success字段为false，则抛出错误以便在catch中处理
        if (data && data.success === false) {
            if (isAuthRequest || isCroomRequest) {
                console.group(`%c响应错误: ${config.method.toUpperCase()} ${config.url}`, 
                            'background:#f44336; color:#fff; padding:2px 4px;');
                console.log('错误码:', data.code);
                console.log('错误消息:', data.message);
                console.log('完整错误数据:', data);
                console.groupEnd();
            }
            
            const error = new Error(data.message || '请求失败');
            error.code = data.code;
            error.data = data;
            return Promise.reject(error);
        }
        
        // 成功请求返回数据
        return data;
    },
    async error => {
        const originalRequest = error.config;
        
        console.group(`%c响应错误处理`, 'background:#ff9800; color:#fff; padding:2px 4px;');
        console.log('错误时间:', new Date().toISOString());
        console.log('请求配置:', originalRequest);
        
        if (error.response) {
            console.log('HTTP状态码:', error.response.status);
            console.log('响应数据:', error.response.data);
        } else if (error.request) {
            console.log('未收到响应:', error.request);
        } else {
            console.log('请求错误:', error.message);
        }
        
        // 处理401错误（未授权，令牌过期）
        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            console.log('检测到401未授权错误，尝试刷新令牌');
            // 标记请求已尝试重试
            originalRequest._retry = true;
            
            // 如果不是刷新令牌的请求本身
            if (!originalRequest.url.includes('/auth/refresh')) {
                try {
                    // 如果已经在刷新令牌中，则将请求加入队列
                    if (isRefreshing) {
                        return new Promise((resolve) => {
                            subscribeTokenRefresh(token => {
                                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                                resolve(service(originalRequest));
                            });
                        });
                    }
                    
                    // 标记正在刷新令牌
                    isRefreshing = true;
                    
                    try {
                        const result = await userService.refreshToken();
                        if (result) {
                            // 如果成功刷新，更新原始请求的令牌并重试
                            originalRequest.headers['Authorization'] = `Bearer ${result.access_token}`;
                            
                            // 通知所有等待的请求令牌已刷新
                            onRefreshed(result.access_token);
                            
                            // 重置刷新状态
                            isRefreshing = false;
                            
                            // 关键改进：使用新token重试原始请求，不抛出错误
                            console.log('Token刷新成功，重试原始请求:', originalRequest.url);
                            console.groupEnd(); // 关闭日志组
                            return service(originalRequest);
                        }
                    } catch (refreshError) {
                        console.log('刷新令牌错误:', refreshError);
                        
                        // 检查是否是刷新令牌错误
                        if (refreshError.isRefreshTokenError) {
                            console.log('刷新令牌已过期或无效，需要重新登录');
                            // 清除用户状态
                            userService.logout();
                            // 提示用户重新登录
                            showLoginModal();
                        } else {
                            console.log('刷新令牌失败:', refreshError);
                            // 其他错误，也需要清除状态并重新登录
                            userService.logout();
                            showLoginModal();
                        }
                        
                        // 重置刷新状态
                        isRefreshing = false;
                        
                        // 关键改进：忽略原始请求的错误，返回特殊的Promise，与业务逻辑区分开
                        console.groupEnd(); // 关闭日志组
                        return Promise.reject({
                            isAuthError: true,
                            message: '认证失败，需要重新登录'
                        });
                    }
                } catch (e) {
                    console.error('令牌刷新过程中发生错误:', e);
                    // 重置刷新状态
                    isRefreshing = false;
                    // 清除用户状态
                    userService.logout();
                    // 提示用户重新登录
                    showLoginModal();
                    
                    // 关键改进：忽略原始请求的错误，返回特殊的Promise，与业务逻辑区分开
                    console.groupEnd(); // 关闭日志组
                    return Promise.reject({
                        isAuthError: true,
                        message: '认证过程中发生错误'
                    });
                }
            } else {
                // 如果是刷新令牌请求返回401，说明刷新令牌无效
                console.log('刷新令牌请求返回401，刷新令牌已失效');
                // 清除用户状态
                userService.logout();
                // 提示用户重新登录
                showLoginModal();
                
                // 关键改进：返回特殊的Promise，与业务逻辑区分开
                console.groupEnd(); // 关闭日志组
                return Promise.reject({
                    isAuthError: true,
                    message: '刷新令牌已过期，请重新登录'
                });
            }
        }
        
        // 处理其他错误
        if (error.response) {
            // 服务器返回了错误状态码
            const errorData = error.response.data;
            
            // 适配统一Result格式的错误响应
            if (errorData && typeof errorData === 'object') {
                // 如果响应中有success字段和message字段，则使用这些字段
                if ('success' in errorData && errorData.success === false) {
                    const formattedError = new Error(errorData.message || '请求失败');
                    formattedError.code = errorData.code;
                    formattedError.data = errorData;
                    console.groupEnd(); // 关闭日志组
                    return Promise.reject(formattedError);
                }
            }
            
            // 可以根据不同的错误状态码进行不同的处理
            if (error.response.status === 403) {
                console.error('没有权限访问此资源');
            } else if (error.response.status === 404) {
                console.error('请求的资源不存在');
            } else if (error.response.status === 500) {
                console.error('服务器内部错误');
            }
            
            console.groupEnd(); // 关闭日志组
            return Promise.reject(errorData || error);
        } else if (error.request) {
            // 请求已发送但未收到响应
            console.error('未收到服务器响应');
            console.groupEnd(); // 关闭日志组
            return Promise.reject({ success: false, message: '网络异常，请检查网络连接', code: 0 });
        } else {
            // 请求设置中发生错误
            console.error('请求设置错误:', error.message);
            console.groupEnd(); // 关闭日志组
            return Promise.reject({ success: false, message: '请求错误', code: 0 });
        }
    }
);

// 封装的请求方法
export default {
    // GET请求
    get(url, params, config = {}) {
        return service({
            url,
            method: 'get',
            params,
            ...config
        });
    },
    
    // POST请求
    post(url, data, config = {}) {
        return service({
            url,
            method: 'post',
            data,
            ...config
        });
    },
    
    // PUT请求
    put(url, data, config = {}) {
        return service({
            url,
            method: 'put',
            data,
            ...config
        });
    },
    
    // DELETE请求
    delete(url, params, config = {}) {
        return service({
            url,
            method: 'delete',
            params,
            ...config
        });
    },
    getCurrentUser,
    
    // 请求实例，用于自定义请求
    service
};
